/**
 * Special-day / special-week template standard.
 *
 * Every campaign week or day uses the same three layers:
 *   1. Banner — campaign mark washed out behind the copy (Te Wiki tiki location).
 *   2. Platform theme + AI tone — colours and copy shift to the kaupapa.
 *   3. Slogan — English + te reo when the campaign publishes one.
 *
 * Create writes a week template or a day template from the event dates.
 * Pink Shirt Day is one Friday; anti-bullying skin can still run that week.
 * “Create week” / “Create day” on the calendar must follow CAMPAIGN_TEMPLATE_RULES.
 */

import { occurrencesForYear } from './calendar';
import {
  CAMPAIGN_TEMPLATE_SLUGS,
  campaignHasCutOver,
  isCampaignTemplateSlug,
  type CampaignTemplateSlug,
} from './campaignTemplates';
import { teWikiSwimAiPlanHint } from './teWikiKupu';
import { addDaysIso, dayOfWeekIso, nzHourOfDay, nzWallClock } from './time';

/** Locked standard for every special day / week template (AI + coded). */
export const CAMPAIGN_TEMPLATE_RULES = [
  '1. Banner — week or day logo (if there is one), washed out behind the copy in the same place as Te Wiki (right side of the campaign strip).',
  '2. AI adjustment of content on the platform based on the day/week theme. Example: Pink Shirt Day (anti-bullying) turns the site pink and carries anti-bullying sentiments throughout that week.',
  '3. Slogan or theme — English plus te reo when the campaign publishes one. Example: Mental Health Awareness Week — Belonging Matters / Ko au ko koe, ko koe ko au.',
].join('\n');

export const CAMPAIGN_TEMPLATE_RULES_SUMMARY =
  'Banner logo washed out like Te Wiki · AI restyles the platform to the kaupapa · slogan in English and te reo.';

export interface CampaignThemeDef {
  slug: CampaignTemplateSlug;
  slogan: string;
  sloganMi: string | null;
  artUrl: string;
  themeColor: string;
  /** When true, a single-day event skins Mon–Sun of that week. */
  themeWholeWeek: boolean;
  sentiments: string[];
  bannerGreeting: string;
  bannerKicker: string;
  bannerKupu: Array<{ word: string; gloss: string }>;
  motif: 'koru' | 'heart' | 'pink';
  aiHint: string;
  assistantHint: string;
}

export const CAMPAIGN_THEMES: Record<CampaignTemplateSlug, CampaignThemeDef> = {
  'te-wiki-o-te-reo-maori': {
    slug: 'te-wiki-o-te-reo-maori',
    slogan: 'Ake, ake, ake',
    sloganMi: 'Ake, ake, ake',
    artUrl: '/te-wiki-tiki.webp',
    themeColor: '#15803d',
    themeWholeWeek: false,
    sentiments: [
      'Kia ora — use the kupu, keep the tohutō.',
      'Name one set in te reo today: kikī, kume, or ruku.',
      'Greet every ākonga by name. Ka rawe.',
    ],
    bannerGreeting: 'Nau mai ki Te Wiki o te Reo Māori',
    bannerKicker: 'Ake, ake, ake — use the kupu, keep the tohutō.',
    bannerKupu: [
      { word: 'kia ora', gloss: 'hello' },
      { word: 'kaukau', gloss: 'swim' },
      { word: 'ruku', gloss: 'dive' },
      { word: 'kikī', gloss: 'kick' },
      { word: 'kume', gloss: 'pull' },
      { word: 'raina', gloss: 'lane' },
      { word: 'tere', gloss: 'fast' },
      { word: 'ka rawe', gloss: 'excellent' },
    ],
    motif: 'koru',
    aiHint: teWikiSwimAiPlanHint(),
    assistantHint:
      'Te Wiki o te Reo Māori is on. Use te reo with tohutō in greetings (kia ora, kia kaha, ka rawe) and swimming kupu (kaukau, ruku, kikī, kume, raina). Keep English glosses when a kupu might be new.',
  },
  'mental-health-awareness-week': {
    slug: 'mental-health-awareness-week',
    slogan: 'Belonging Matters',
    sloganMi: 'Ko au ko koe, ko koe ko au',
    artUrl: '/campaigns/mhaw-mark.webp',
    themeColor: '#0d9488',
    themeWholeWeek: false,
    sentiments: [
      'Belonging Matters — you have a place in this lane.',
      'Ko au ko koe, ko koe ko au — I am you, you are me.',
      'Check in, not just the clock.',
      'A quiet word beats a louder set.',
      'Everyone who made it to the wall already belongs.',
    ],
    bannerGreeting: 'Belonging Matters',
    bannerKicker: 'Ko au ko koe, ko koe ko au — I am you, you are me.',
    bannerKupu: [
      { word: 'Belonging Matters', gloss: '2026 kaupapa' },
      { word: 'Ko au ko koe, ko koe ko au', gloss: 'I am you, you are me' },
      { word: 'manaakitanga', gloss: 'care' },
      { word: 'whānau', gloss: 'family' },
    ],
    motif: 'heart',
    aiHint:
      'For Mental Health Awareness Week, session_idea and coach_reminder MUST use the official kaupapa Belonging Matters / Ko au ko koe, ko koe ko au. Prefer check-ins, belonging language, and no public calling-out of times. Keep it warm, not clinical.',
    assistantHint:
      'Mental Health Awareness Week is on. Theme: Belonging Matters / Ko au ko koe, ko koe ko au (I am you, you are me). Speak with care. Emphasise belonging, check-ins, and that every swimmer has a place in the lane. Never joke about mental health or tell anyone to toughen up.',
  },
  'pink-shirt-day': {
    slug: 'pink-shirt-day',
    slogan: 'Be an upstander',
    sloganMi: 'Kia tū hei kaihāpai',
    artUrl: '/campaigns/pink-shirt-mark.webp',
    themeColor: '#ec4899',
    themeWholeWeek: true,
    sentiments: [
      'Be an upstander — kindness in every lane.',
      'No bullying on this deck. Cheer the person, not the roast.',
      'Wear pink. Name people in. Leave no one on the wall alone.',
      'Kia tū hei kaihāpai — stand up for your mates.',
      'If it would sting on the poolside, do not say it.',
    ],
    bannerGreeting: 'Be an upstander',
    bannerKicker: 'Wear pink. No bullying in this lane — kindness first.',
    bannerKupu: [
      { word: 'Be an upstander', gloss: 'not a bystander' },
      { word: 'manaakitanga', gloss: 'care / kindness' },
      { word: 'Kia tū hei kaihāpai', gloss: 'stand as a supporter' },
    ],
    motif: 'pink',
    aiHint:
      'For Pink Shirt Day (anti-bullying week), session_idea and coach_reminder MUST keep the platform and the pool pink-themed: wear pink, kindness relays, names used, no roasting. Slogan: Be an upstander / Kia tū hei kaihāpai. Anti-bullying sentiments belong in every session that week, not only Friday.',
    assistantHint:
      'Pink Shirt Day week is on — anti-bullying. The site is pink on purpose. Theme: Be an upstander / Kia tū hei kaihāpai. Talk kindness, names-used, and standing up for mates. Never minimise bullying. Invite pink kit for Friday especially.',
  },
};

/** Monday (ISO week) containing the NZ civil date. */
export function mondayOfContainingWeek(date: string): string {
  const dow = dayOfWeekIso(date); // 0=Sun
  const delta = dow === 0 ? -6 : 1 - dow;
  return addDaysIso(date, delta);
}

export function sundayOfContainingWeek(date: string): string {
  return addDaysIso(mondayOfContainingWeek(date), 6);
}

export type CalendarCutoverDates = {
  occursOn: string;
  endsOn?: string | null;
};

/** Live window follows the calendar row (or catalog). Not the whole ISO week. */
export function campaignThemeWindow(
  slug: CampaignTemplateSlug,
  year: number,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
  calendar?: CalendarCutoverDates | null,
): { start: string; end: string } | null {
  if (calendar?.occursOn) {
    return { start: calendar.occursOn, end: calendar.endsOn ?? calendar.occursOn };
  }
  const occ = occurrencesForYear(year, region).find((o) => o.slug === slug);
  if (!occ) return null;
  return { start: occ.occursOn, end: occ.endsOn ?? occ.occursOn };
}

export function isCampaignThemeActive(
  slug: CampaignTemplateSlug,
  today: string = nzWallClock().isoDate,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
  calendar?: CalendarCutoverDates | null,
): boolean {
  if (previewSlug) return previewSlug === slug;
  const year = parseInt(today.slice(0, 4), 10);
  if (!Number.isFinite(year)) return false;
  const window = campaignThemeWindow(slug, year, 'northland', calendar);
  if (!window) return false;
  return campaignHasCutOver(window.start, window.end, today, nzHour);
}

export function activeCampaignSlug(
  today: string = nzWallClock().isoDate,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
  calendarBySlug?: Partial<Record<CampaignTemplateSlug, CalendarCutoverDates | null>> | null,
): CampaignTemplateSlug | null {
  if (previewSlug && isCampaignTemplateSlug(previewSlug)) return previewSlug;
  for (const slug of CAMPAIGN_TEMPLATE_SLUGS) {
    if (isCampaignThemeActive(slug, today, null, nzHour, calendarBySlug?.[slug])) return slug;
  }
  return null;
}

export function activeCampaignSlugFromObservances(
  events: Array<{ slug: string; occursOn: string; endsOn?: string | null }>,
  today: string = nzWallClock().isoDate,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
): CampaignTemplateSlug | null {
  const calendarBySlug: Partial<Record<CampaignTemplateSlug, CalendarCutoverDates>> = {};
  for (const ev of events) {
    if (!isCampaignTemplateSlug(ev.slug)) continue;
    calendarBySlug[ev.slug] = { occursOn: ev.occursOn, endsOn: ev.endsOn ?? null };
  }
  return activeCampaignSlug(today, previewSlug, nzHour, calendarBySlug);
}

export function activeCampaignTheme(
  today: string = nzWallClock().isoDate,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
  calendarBySlug?: Partial<Record<CampaignTemplateSlug, CalendarCutoverDates | null>> | null,
): CampaignThemeDef | null {
  const slug = activeCampaignSlug(today, previewSlug, nzHour, calendarBySlug);
  return slug ? CAMPAIGN_THEMES[slug] : null;
}

export function activeCampaignThemeFromObservances(
  events: Array<{ slug: string; occursOn: string; endsOn?: string | null }>,
  today: string = nzWallClock().isoDate,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
): CampaignThemeDef | null {
  const slug = activeCampaignSlugFromObservances(events, today, previewSlug, nzHour);
  return slug ? CAMPAIGN_THEMES[slug] : null;
}

export function campaignThemeBySlug(slug: string | null | undefined): CampaignThemeDef | null {
  if (!isCampaignTemplateSlug(slug)) return null;
  return CAMPAIGN_THEMES[slug];
}

/** Stable per NZ date so the same sentiment shows all day. */
export function campaignSentimentForDate(
  theme: CampaignThemeDef,
  today: string = nzWallClock().isoDate,
): string {
  const [y, m, d] = today.split('-').map((s) => parseInt(s, 10));
  const idx = Number.isFinite(y)
    ? Math.floor((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / 86400000)
    : 0;
  return theme.sentiments[Math.abs(idx) % theme.sentiments.length];
}

export function campaignAiPlanHint(): string {
  return CAMPAIGN_TEMPLATE_SLUGS.map((slug) => CAMPAIGN_THEMES[slug].aiHint).join(' ');
}

export function campaignAssistantHint(
  today: string = nzWallClock().isoDate,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
): string | null {
  return activeCampaignTheme(today, previewSlug, nzHour)?.assistantHint ?? null;
}

export function formatCampaignSlogan(theme: CampaignThemeDef): string {
  if (theme.sloganMi && theme.sloganMi !== theme.slogan) {
    return `${theme.slogan} / ${theme.sloganMi}`;
  }
  return theme.slogan;
}
